import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import PageLayout from '../../layouts/PageLayout';
import { usePageTitle } from '../../hooks/usePageTitle';
import { HeroRibbon } from '../Home/components/HeroSection';
import { Section } from '../../components/shared/Section';
import { useEnhancedHealthSummary } from '../../hooks/useEnhancedHealthSummary';
import { useAnnotations } from '../../hooks/useAnnotations';
import EnhancedHealthSummaryWidget from './components/EnhancedHealthSummaryWidget';
import FoodLogCard from './components/FoodLogCard';
import GlucoseTrendsCard from './components/GlucoseTrendsCard';

export default function CtankHealthSummary() {
  usePageTitle('Health Summary');
  const { data: healthSummary, loading, error } = useEnhancedHealthSummary();
  const { annotations, loading: annotationsLoading } = useAnnotations();

  // Override the manifest when component mounts
  useEffect(() => {
    // Remove existing manifest link
    const existingManifest = document.querySelector('link[rel="manifest"]');
    if (existingManifest) {
      existingManifest.remove();
    }

    // Add Health-specific manifest
    const healthManifest = document.createElement('link');
    healthManifest.rel = 'manifest';
    healthManifest.href = '/health-manifest.json';
    document.head.appendChild(healthManifest);

    // Cleanup when component unmounts
    return () => {
      const healthManifestLink = document.querySelector('link[href="/health-manifest.json"]');
      if (healthManifestLink) {
        healthManifestLink.remove();
      }
      
      // Restore main manifest
      const mainManifest = document.createElement('link');
      mainManifest.rel = 'manifest';
      mainManifest.href = '/manifest.json';
      document.head.appendChild(mainManifest);
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>🩺 Chintan's Health Analytics</title>
        <meta name="description" content="Personal health dashboard with blood sugar tracking, weight monitoring, and exercise analytics. Automated health insights and trends." />
        <meta name="theme-color" content="#10b981" />
        
        {/* Health-specific icons */}
        <link rel="icon" href="/health-icon-32.png" sizes="32x32" />
        <link rel="icon" href="/health-icon-192.png" sizes="192x192" />
        <link rel="apple-touch-icon" href="/health-icon-192.png" />
        
        {/* Override app title for iOS */}
        <meta name="apple-mobile-web-app-title" content="Health Analytics" />
        <meta name="application-name" content="Health Analytics" />
        
        {/* Open Graph for sharing */}
        <meta property="og:title" content="🩺 Chintan's Health Analytics" />
        <meta property="og:description" content="Personal health dashboard with automated blood sugar, weight, and exercise tracking." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://chintantank.info/tools/health/ctank-summary" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="🩺 Chintan's Health Analytics" />
        <meta name="twitter:description" content="Personal health dashboard with automated tracking and insights." />
      </Helmet>

      <PageLayout>
        {/* Hero Section */}
        <HeroRibbon
          title="Health Tracking Dashboard"
          subtitle="Automated summary of blood sugar, weight, and exercise trends."
        />

        {/* Main Health Summary */}
        <Section background="light" spacing="sm" withDivider={true}>
          <div className="mb-10">
            <EnhancedHealthSummaryWidget healthSummary={healthSummary} loading={loading} error={error} />
          </div>

          {/* Glucose Trends Card (SVG, real data) */}
          {healthSummary && (() => {
            // Gather all values from all types, and add type info
            const allReadings = [
              ...(healthSummary.fasting?.values?.map(r => ({ ...r, type: 'fasting' })) || []),
              ...(healthSummary.preMeal?.values?.map(r => ({ ...r, type: 'pre-meal' })) || []),
              ...(healthSummary.postMeal?.values?.map(r => ({ ...r, type: 'post-meal' })) || []),
              ...(healthSummary.random?.values?.map(r => ({ ...r, type: 'random' })) || []),
            ];
            // Filter for last 30 days
            const now = new Date();
            const lastMonth = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            const filtered = allReadings.filter(r => {
              const d = new Date(r.date);
              return d >= lastMonth && d <= now && r.value;
            });
            // Format for the card
            const readings = filtered.map(r => ({
              value: r.value,
              type: r.type,
              date: new Date(r.date),
            })).sort((a, b) => a.date.getTime() - b.date.getTime());
            if (readings.length === 0) return null;

            
            return <GlucoseTrendsCard readings={readings} annotations={annotations}/>;
          })()}

          {healthSummary?.food && (
            <div className="mt-10">
              <FoodLogCard foodData={healthSummary.food} />
            </div>
          )}
        </Section>
      </PageLayout>
    </>
  );
}